/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { USER_LEAVE_CANCEL, USER_MYLEAVE } from "../../service/apiUrl";
import { postApiCall } from "../../service/PostApiCall";
import DataTable from "../../components/table/DataTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import MorphTabs from "../../components/MorphTabs";
import {
  LeaveCell,
  LEAVE_STATUS_TABS as STATUS_TABS,
  LeaveToneLegend,
  leaveDateRange,
  leaveKind,
  leaveTone,
  statusKey,
} from "./leaveTone";

/* The same shape as the admin's Manage Leave: status tabs over the table,
   one "Leave" cell, the dates on their own. The list here is the employee's
   whole history (client mode), so the tabs filter and count in the browser. */


/* The year the leave FALLS IN, not the year it was applied for: a December
   request for January sits in January's row of a leave balance. Rows without
   a usable `from_timestamp` fall back to the year in the composed type text,
   and only then to nothing, which the facet drops. */
const leaveYear = (leave) => {
  const from = Number(leave?.from_timestamp);
  if (from > 0) return moment.unix(from).format("YYYY");
  const match = String(leave?.type ?? "").match(/\b(20\d{2})\b/);
  return match ? match[1] : "";
};

const MyLeaves = (props) => {
  const [cancelLeaveId, setCancelLeaveId] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState("all");
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;

  useEffect(() => {
    getLeaves();
  }, []);

  /* The endpoint paginates (10 by default) and this table pages in the
     browser, so ask for the whole history; without a page size an employee
     only ever saw their ten newest leaves. */
  const getLeaves = () =>
    postApiCall({ page: 1, pageSize: 1000 }, access_token, USER_MYLEAVE)
      .then((response) => {
        setLeaves(response?.leaves ?? []);
        setBalance(response?.leave_summary ?? null);
      })
      .catch((error) => {
        console.error("MY_LEAVE", error);
      })
      .finally(() => {
        setLoaded(true);
      });

  const cancelLeave = () => {
    if (!cancelLeaveId) return false;
    return postApiCall({ id: cancelLeaveId }, access_token, USER_LEAVE_CANCEL)
      .then(() => {
        getLeaves();
        setCancelLeaveId("");
      })
      .catch((error) => {
        console.error("Cancel_leave", error);
        throw error;
      });
  };

  const tabOf = (leave) =>
    STATUS_TABS.find((tab) => tab.keys && tab.keys.includes(statusKey(leave?.status)));

  const statusItems = useMemo(
    () =>
      STATUS_TABS.map((tab) => ({
        value: tab.value,
        label: tab.label,
        tone: tab.tone,
        count: tab.keys ? leaves.filter((leave) => tabOf(leave) === tab).length : leaves.length,
      })),
    [leaves]
  );

  const rows = useMemo(
    () => (status === "all" ? leaves : leaves.filter((leave) => tabOf(leave)?.value === status)),
    [leaves, status]
  );

  const pendingCount = statusItems.find((tab) => tab.value === "pending")?.count ?? 0;

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "#",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "56px" },
        /* `row.index` is the position in the unsorted source array, so it
           renumbers nothing when the table is sorted or filtered and repeats
           itself across pages. `getDisplayIndex()` is the row's place in what
           the user is actually looking at. */
        cell: ({ row }) => (
          <span className="text-nowrap text-muted">{row.getDisplayIndex() + 1}</span>
        ),
      },
      {
        id: "kind",
        accessorFn: (row) => leaveKind(row?.type),
        header: "Leave",
        enableHiding: false,
        cell: ({ row }) => <LeaveCell leave={row.original} />,
      },
      {
        id: "dates",
        accessorFn: (row) => Number(row?.from_timestamp) || 0,
        header: "Dates",
        cell: ({ row }) => (
          <span className="text-nowrap">{leaveDateRange(row.original) || "-"}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <div>
            <span className={"status-pill s-" + statusKey(row.original?.status)}>
              {row.original?.status}
            </span>
            {row.original?.comments ? (
              <div className="text-muted mt-1" style={{ fontSize: "12px", maxWidth: "260px" }}>
                {row.original.comments}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: "updated_by",
        accessorFn: (row) => row?.updated_by?.name ?? "",
        header: "Updated by",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.updated_by?.name || "-"}</span>
        ),
      },
      {
        id: "created_at",
        accessorFn: (row) => Number(row?.created_at) || 0,
        header: "Applied",
        cell: ({ row }) => (
          <span className="text-nowrap text-muted">
            {Number(row.original?.created_at) > 0
              ? moment.unix(row.original.created_at).format("MMM D, YYYY")
              : "-"}
          </span>
        ),
      },
      {
        id: "year",
        accessorFn: (row) => leaveYear(row),
        header: "Year",
        meta: { width: "80px" },
        cell: ({ row }) => (
          <span className="text-nowrap">{leaveYear(row.original) || "-"}</span>
        ),
      },
      {
        id: "manage",
        header: "",
        enableSorting: false,
        enableHiding: false,
        meta: { className: "text-right" },
        cell: ({ row }) =>
          statusKey(row.original?.status) === "pending" ? (
            <button
              type="button"
              className="dt-btn"
              onClick={() => setCancelLeaveId(row.original?.id)}
            >
              <i className="fa fa-ban" aria-hidden="true"></i>
              Cancel
            </button>
          ) : null,
      },
    ],
    []
  );

  const activeTab = STATUS_TABS.find((tab) => tab.value === status);

  return (
    <>
      <div className={props?.data?.transtion == true ? "contents" : "content"}>
        <div className="content-top-spacer"></div>
        <div className="color-body">
          <div className="page-head page-head-row">
            <div>
              <div className="page-eyebrow">People</div>
              <h1 className="page-title">My Leaves</h1>
              <p className="page-sub">
                {balance
                  ? `${balance.balance} paid leave${balance.balance === 1 ? "" : "s"} available`
                  : `${leaves.length} leave${leaves.length === 1 ? "" : "s"}`}
                {pendingCount ? ` · ${pendingCount} awaiting approval` : ""}
              </p>
            </div>
            <div className="page-head-actions">
              <Link to="/add-leave" className="dt-btn dt-btn-primary">
                <i className="fa fa-plus" aria-hidden="true"></i>
                Apply for leave
              </Link>
            </div>
          </div>
          <div className="col-md-12">
            <div className="card-input mt-3">
              <div className="col-md-12 mb-3">
                <div className="mt-4">
                  <div className="ml-statusbar">
                    <MorphTabs
                      label="Leave status"
                      items={statusItems}
                      value={status}
                      onChange={setStatus}
                    />
                    <LeaveToneLegend />
                  </div>
                  <DataTable
                    columns={columns}
                    data={rows}
                    isLoading={!loaded}
                    getRowId={(row, index) => String(row?.id ?? index)}
                    search={{
                      columns: ["kind", "status", "updated_by"],
                      placeholder: "Search leaves...",
                    }}
                    /* Year rather than month: FacetFilter sorts its values
                       with localeCompare, which puts "Apr" before "Jan",
                       and a year list stays four entries long where a
                       month list grows twelve a year. */
                    facets={[
                      { columnId: "kind", label: "Type" },
                      { columnId: "year", label: "Year" },
                    ]}
                    hiddenColumns={["year"]}
                    rowTone={(row) => leaveTone(row.original)}
                    pagination={{
                      pageSize: 10,
                      pageSizeOptions: [10, 25, 50, 100],
                    }}
                    emptyText={
                      status === "all"
                        ? "No leave found"
                        : `No ${activeTab?.label.toLowerCase()} leave`
                    }
                  />

                  <ConfirmDialog
                    isShow={Boolean(cancelLeaveId)}
                    onHide={() => setCancelLeaveId("")}
                    title="Cancel this leave?"
                    description="The request is withdrawn and leaves the listing. A cancelled leave cannot be restored."
                    confirmLabel="Cancel leave"
                    successLabel="Cancelled"
                    cancelLabel="Keep leave"
                    tone="danger"
                    onConfirm={cancelLeave}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyLeaves;
